import { createElement, FocusEvent, forwardRef, InputHTMLAttributes, useCallback, useId, useMemo } from "react";
import classNames from "classnames";
import { RefType } from "../../../types";
import { genericMemo } from "../../../utils";
import { ExpectedInputProps, InputContainerComponentType, InputContainerProps, InputContainerState } from "./types";

import { ReactComponent as AlertIcon } from "./icons/alert.svg";
import { ReactComponent as ClearIcon } from "./icons/clear.svg";

import styles from "./styles.module.scss";

export const InputContainer = genericMemo(forwardRef(<P extends ExpectedInputProps = InputHTMLAttributes<HTMLInputElement>, R extends unknown = HTMLInputElement>(
  allProps: InputContainerProps<P, R>,
  ref: RefType<R>,
) => {
  const {
    inputComponent = "input",
    inputState = InputContainerState.default,
    inputSize = "sm",
    withFocusWithin = true,
    label,
    hintText,
    mapPropsToInput = ({ inputProps }) => inputProps,
    onClear,
    additionalOnBlurCheck,
    isClearable,
    renderClearButtonOnEmptyState,
    inFocusedState,
    hideHintText,
    hideErrorIcon,

    inputClassName,
    rootContainerProps,
    labelProps,
    containerProps,
    inputContainerProps,
    clearButtonProps,
    hintProps,

    startContainerAdornment,
    endContainerAdornment,
    startAdornment,
    endAdornment,
    additionalInputContent,

    ...props
  } = allProps;
  const reactId = useId();
  const realId = useMemo(() => props.id || reactId, [ props.id, reactId ]);
  const hasValue = `${props.value}` === "0" || !!props.value;
  const renderClearButton = (hasValue || renderClearButtonOnEmptyState) && !props.disabled && isClearable;

  const onClearButtonClick = useCallback(() => {
    if (!props.disabled) {
      if (onClear) {
        onClear();
      } else {
        return props?.onChange?.(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ props.disabled, onClear, props.onChange ]);

  const onContainerBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Node)) {
      if (additionalOnBlurCheck) {
        if (additionalOnBlurCheck(e)) {
          return rootContainerProps?.onBlur?.(e);
        }
      } else {
        return rootContainerProps?.onBlur?.(e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ rootContainerProps?.onBlur, additionalOnBlurCheck ]);

  return (
    <div
      key="root"
      {...rootContainerProps}
      className={classNames(
        rootContainerProps?.className,
        styles.root,
        styles[inputSize as keyof typeof styles],
        styles[inputState as keyof typeof styles],
        {
          [styles.disabled]: props?.disabled,
          [styles.focused]: inFocusedState,
          [styles.withFocusWithin]: withFocusWithin,
        },
      )}
      onBlur={onContainerBlur}
      data-input-disabled={`${props.disabled}`}
      data-input-state={inputState}
    >
      {label &&
        <label
          key="label"
          {...labelProps}
          className={classNames(labelProps?.className, styles.label)}
          htmlFor={`${realId}`}>
          {label}
        </label>
      }
      <div
        key="container"
        {...containerProps}
        className={classNames(containerProps?.className, styles.container)}
      >
        {typeof startContainerAdornment === "string" ? <span key="startContainerAdornment">{startContainerAdornment}</span> : startContainerAdornment}
        <div key="inputContainer" {...inputContainerProps} className={classNames(inputContainerProps?.className, styles.inputContainer)}>
          {typeof startAdornment === "string" ? <span key="startAdornment">{startAdornment}</span> : startAdornment}
          {
            createElement(
              inputComponent,
              {
                ...mapPropsToInput({
                  allProps,
                  inputProps: {
                    ...props as P,
                    id: realId,
                    className: classNames(styles.input, inputClassName),
                    ref,
                  },
                }),
                key: "input",
              },
            )
          }
          {additionalInputContent}
          {renderClearButton &&
            <button
              key="clear"
              {...clearButtonProps}
              className={classNames(clearButtonProps?.className, styles.clearButton)}
              type="button"
              onClick={onClearButtonClick}
            >
              <ClearIcon />
            </button>
          }
          {typeof endAdornment === "string" ? <span key="endAdornment">{endAdornment}</span> : endAdornment}
          {(inputState === "error" && !hideErrorIcon) && <AlertIcon key="error" />}
        </div>
        {typeof endContainerAdornment === "string" ? <span key="endContainerAdornment">{endContainerAdornment}</span> : endContainerAdornment}
      </div>
      {(!!hintText && !hideHintText) &&
        <span
          key="hintText"
          {...hintProps}
          className={classNames(hintProps?.className, styles.hintText)}
          title={typeof hintText === "string" ? hintText : undefined}
        >
          {hintText}
        </span>
      }
    </div>
  );
}) as InputContainerComponentType);
